import { useMemo, useState } from 'react'
import { UserPlus } from 'lucide-react'
import { useAuth } from '../../core/auth'
import { ROLES, canGrant, grantString, grantsToRemove, holdsRole } from '../../core/caps'
import { useUsers, setCaps, seenLabel, toDate, useRoster, type UserRow } from '../admin/model'
import { AddPerson, type Actor } from '../admin/Person'
import { useNorseTerm } from './NorseTermData'
import { Loading, ErrorState, Empty } from '../../ui/States'

/**
 * NorseTerm staff: who is Admin, Office and Teacher in Norse Term, set here
 * without going through the whole Accounts screen. Same writes and the same
 * rules as Accounts (setCaps, checked by firestore.rules: you can only hand
 * out what you hold, never change yourself), at the high school scope where
 * Norse Term lives. Next to each person: their course and how many seminar
 * students they have, so "who is doing what" is one page.
 */
const NT_ROLES = ROLES.filter((r) => r.tool === 'norseterm')
const SCOPE = 'hs' as const

export function NorseTermStaff() {
  const { profile, user } = useAuth()
  const { courses } = useNorseTerm()
  const { rows, err } = useUsers(true)
  const { rows: students } = useRoster(true)
  const [show, setShow] = useState<'nt' | 'all'>('nt')
  const [q, setQ] = useState('')
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  const me = (user?.email ?? '').toLowerCase()
  const who = profile?.displayName ?? me
  const actor: Actor = { email: me, caps: profile?.caps, districtAdmin: profile?.districtAdmin, mayEditDirectory: false }
  const grantable = NT_ROLES.filter((r) => r.caps.every((c) => canGrant(actor, c, SCOPE)))

  const seminarCount = useMemo(() => {
    const m = new Map<string, number>()
    for (const s of students ?? []) if (s.active && s.seminarTeacherEmail) m.set(s.seminarTeacherEmail, (m.get(s.seminarTeacherEmail) ?? 0) + 1)
    return m
  }, [students])

  if (err) return <ErrorState body={`Could not load staff. ${err}`} />
  if (!rows) return <Loading label="Loading staff" />
  if (adding) {
    return <AddPerson scopes={[SCOPE]} actor={actor} who={who} existing={rows}
      onDone={() => setAdding(false)} />
  }

  const inNt = (r: UserRow) => !!r.districtAdmin || NT_ROLES.some((role) => holdsRole(r.caps, role, SCOPE))
    || (r.caps ?? []).some((c) => c.startsWith('norseterm.'))
  const shown = rows
    .filter((r) => r.active !== false)
    .filter((r) => show === 'all' || inNt(r))
    .filter((r) => !q || `${r.displayName} ${r.email}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
  const adminRole = NT_ROLES.find((x) => x.id === 'norseterm.admin')!
  // Office is counted without the Admins who include it, or the totals double up.
  const count = (role: (typeof NT_ROLES)[number]) => rows.filter((r) => r.active !== false && holdsRole(r.caps, role, SCOPE)
    && !(role.id === 'norseterm.office' && holdsRole(r.caps, adminRole, SCOPE))).length
  const courseOf = (email: string) => (courses ?? []).find((c) => c.teacherEmail === email)

  const toggle = async (r: UserRow, role: (typeof NT_ROLES)[number], on: boolean) => {
    setBusy(r.id + role.id); setMsg(null)
    const held = r.caps ?? []
    const grants = role.caps.map((c) => grantString(c, SCOPE))
    const next = on ? held.filter((g) => !grantsToRemove(held, role, SCOPE).includes(g)) : [...held, ...grants.filter((g) => !held.includes(g))]
    try { await setCaps(r.id, next, who) }
    catch (e) { setMsg(`${r.displayName} was not changed. ${e instanceof Error ? e.message : String(e)}`) }
    finally { setBusy(null) }
  }

  const box = (r: UserRow, role: (typeof NT_ROLES)[number]) => {
    if (r.districtAdmin) return <span className="work__sub">all</span>
    const self = r.id === me
    const on = holdsRole(r.caps, role, SCOPE)
    // Office inside Admin: held, but unticking it alone removes nothing.
    const within = role.id === 'norseterm.office' && holdsRole(r.caps, NT_ROLES.find((x) => x.id === 'norseterm.admin')!, SCOPE)
    const may = grantable.includes(role) && !self && !within
    return (
      <input type="checkbox" checked={on} disabled={!may || busy === r.id + role.id}
        aria-label={`${r.displayName}: ${role.label}`}
        title={self ? 'Nobody can change their own access' : within ? 'Included in Admin' : role.summary}
        onChange={() => toggle(r, role, on)} />
    )
  }

  return (
    <>
      <p className="ov__p" style={{ marginTop: 'var(--s-2)' }}>
        {NT_ROLES.map((r) => `${count(r)} ${r.label}`).join(' · ')}. Tick a box to give someone that Norse Term role; untick to take it away.
        It saves straight away. <strong>Admin</strong> includes everything Office can do.
      </p>
      {msg && <ErrorState body={msg} />}
      <div className="filters">
        <input className="opt__input" placeholder="Search name or email" aria-label="Search staff" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="chips">
          <button className={`chip${show === 'nt' ? ' is-on' : ''}`} onClick={() => setShow('nt')}>In Norse Term</button>
          <button className={`chip${show === 'all' ? ' is-on' : ''}`} onClick={() => setShow('all')}>All staff</button>
        </div>
        <button className="btn btn--ghost" style={{ width: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setAdding(true)}>
          <UserPlus size={16} aria-hidden="true" /> Add someone
        </button>
      </div>

      {shown.length === 0 ? <Empty title="Nobody here" body="Try All staff, or add someone." /> : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              {NT_ROLES.map((r) => <th key={r.id} className="num">{r.label}</th>)}
              <th>Course</th><th className="num">Seminar</th><th>Last sign-in</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const self = r.id === me
              const course = courseOf(r.id)
              return (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 610 }}>{r.displayName}</div>
                    <div className="work__sub">{r.email}{self ? ' · you' : ''}</div>
                  </td>
                  {NT_ROLES.map((role) => <td key={role.id} className="num">{box(r, role)}</td>)}
                  <td>{course ? <>{course.title || 'Untitled'} <span className="work__sub">· {course.status}</span></> : <span className="work__sub">—</span>}</td>
                  <td className="num">{seminarCount.get(r.id) ?? <span className="work__sub">—</span>}</td>
                  <td className={toDate(r.lastSeenAt) ? '' : 'is-muted'}>{seenLabel(r.lastSeenAt)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
      {shown.length > 0 && (
        <ul className="work cards">
          {shown.map((r) => (
            <li key={r.id} className="work__row" style={{ display: 'block' }}>
              <div style={{ fontWeight: 610 }}>{r.displayName}</div>
              <div className="work__sub">{r.email}</div>
              <div className="chips" style={{ marginTop: 'var(--s-1)' }}>
                {NT_ROLES.map((role) => (
                  <label key={role.id} className="pick" style={{ minHeight: 0, padding: '4px 8px' }}>
                    {box(r, role)} <span className="pick__label">{role.label}</span>
                  </label>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
